import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import { AlertTriangle, CheckCircle2, Clock3, Download, FileText, RotateCcw, Send, Users, Eye, X, CheckCircle, Ban, User, Plus, Coins, Info } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { useAuth } from "@/context/auth.context";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

type Props = {
  month: number;
  year: number;
  onDeclareMonthlyShares: () => void;
};

const SHARE_PRICE = 10000; // 1 share = 10,000 FRW (kept for backward compat; prefer @/utils/shares)

const formatMoney = (value: number) => `${Number(value || 0).toLocaleString()} FRW`;

const formatShares = (shares: number): string => {
  if (!shares || shares === 0) return "0";
  // Trim trailing zeros but keep one decimal if needed
  const trimmed = Number(shares.toFixed(2));
  return trimmed % 1 === 0 ? String(trimmed) : String(trimmed);
};

const statusBadge = (status: string) => {
  if (status === "not_declared" || status === "rejected")
    return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{status === "not_declared" ? "Not Declared" : "Rejected"}</Badge>;
  if (status === "pending_leader_approval")
    return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Awaiting Approval</Badge>;
  if (status === "approved_by_leaders" || status === "ready_for_hr")
    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Ready for HR</Badge>;
  if (status === "sent_to_hr")
    return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Sent to HR</Badge>;
  if (status === "payroll_deducted")
    return <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300">Payroll Deducted</Badge>;
  return <Badge variant="outline">{status.replace(/_/g, " ")}</Badge>;
};

const payrollBadge = (status: string) => {
  if (status === "ready_for_hr" || status === "ready")
    return <Badge className="bg-green-100 text-green-800 border-green-300">Ready for HR</Badge>;
  if (status === "deducted")
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Deducted</Badge>;
  if (status === "failed")
    return <Badge className="bg-red-100 text-red-800 border-red-300">Failed</Badge>;
  return <Badge variant="outline" className="text-gray-500">Pending</Badge>;
};

export default function MonthlyDeclarationControlCenter({ month, year, onDeclareMonthlyShares }: Props) {
  const { user } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "submittedAt", desc: true }]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 15 });
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [declareTarget, setDeclareTarget] = useState<any | null>(null);
  const [declareAmount, setDeclareAmount] = useState<number>(0);
  const [bulkDeclareOpen, setBulkDeclareOpen] = useState(false);
  const [bulkDeclareSelected, setBulkDeclareSelected] = useState<Set<number>>(new Set());
  const [bulkDeclareAmount, setBulkDeclareAmount] = useState<number>(0);

  const overviewQuery = useQuery(
    ["monthly-declaration-overview", month, year],
    async () => {
      const { data } = await api.get("/contributions/monthly-declaration-overview", { params: { month, year } });
      return data?.data ?? data;
    },
    { keepPreviousData: true }
  );
  const overview = overviewQuery.data;
  const allMembers = overview?.members || [];

  const memberHistoryQuery = useQuery(
    ["member-declaration-history", selectedMemberId, month, year],
    async () => {
      if (!selectedMemberId) return null;
      const { data } = await api.get(`/admin/members/${selectedMemberId}/declaration-history`, { params: { month, year } });
      return data?.data ?? data;
    },
    { enabled: !!selectedMemberId }
  );

  const totals = overview?.totals || {};

  const filteredMembers = useMemo(() => {
    let result = [...allMembers];
    const term = searchText.trim().toLowerCase();
    if (term) {
      result = result.filter((m: any) =>
        [m.memberCode, m.memberName, m.phone, m.groupName].some((v) => String(v || "").toLowerCase().includes(term))
      );
    }
    const statusFilter = columnFilters.find((f: any) => f.id === "declarationStatus")?.value;
    if (statusFilter && statusFilter.length > 0) {
      if (statusFilter.includes("declared_by_member")) {
        result = result.filter((m: any) => m.declaredSource === "Member Portal");
      } else if (statusFilter.includes("declared_by_leader")) {
        result = result.filter((m: any) => m.declaredSource && m.declaredSource !== "Member Portal");
      } else {
        result = result.filter((m: any) => statusFilter.includes(m.declarationStatus));
      }
    }
    const approvalFilter = columnFilters.find((f: any) => f.id === "approval")?.value;
    if (approvalFilter && approvalFilter.length > 0) {
      if (approvalFilter.includes("fully_approved")) {
        result = result.filter((m: any) => m.approvalCount >= m.requiredApprovals);
      }
    }
    const payrollFilter = columnFilters.find((f: any) => f.id === "payrollStatus")?.value;
    if (payrollFilter && payrollFilter.length > 0) {
      result = result.filter((m: any) => payrollFilter.includes(m.payrollStatus));
    }
    return result;
  }, [allMembers, searchText, columnFilters]);

  const pageCount = Math.ceil(filteredMembers.length / pageSize);
  const paginatedMembers = useMemo(
    () => filteredMembers.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [filteredMembers, pageIndex, pageSize]
  );

  const runAction = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
      await overviewQuery.refetch();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Unable to complete the action");
    } finally {
      setBusy(false);
    }
  };

  const handleBulkApprove = async (rows: any[]) => {
    const declarationIds = rows
      .filter((r) => r.declarationId && r.declarationStatus === "pending_leader_approval" && !r.alreadyApprovedByCurrentUser && r.approvalCount < r.requiredApprovals)
      .map((r) => r.declarationId);
    if (declarationIds.length === 0) {
      toast.error("No approvable declarations selected");
      return;
    }
    await runAction(async () => {
      const { data } = await api.post("/monthly-saving-declarations/bulk-approve", { declarationIds });
      const { approvedCount, skippedCount, errors } = data;
      const summary = [`Approved ${approvedCount}`];
      if (skippedCount > 0) summary.push(`Skipped ${skippedCount} already approved`);
      if (errors?.length > 0) summary.push(`${errors.length} failed`);
      toast.success(summary.join(". "));
    });
  };

  const selectedMemberData = memberHistoryQuery.data;

  const statusFacetOptions = [
    { label: "Declared", value: "declared" },
    { label: "Not Declared", value: "not_declared" },
    { label: "Awaiting Approval", value: "pending_leader_approval" },
    { label: "Approved", value: "approved_by_leaders" },
    { label: "Ready for HR", value: "ready_for_hr" },
    { label: "Sent to HR", value: "sent_to_hr" },
    { label: "Payroll Processed", value: "payroll_deducted" },
    { label: "Declared by Member", value: "declared_by_member" },
    { label: "Declared by Leader/Admin", value: "declared_by_leader" },
  ];
  const approvalFacetOptions = [{ label: "Fully Approved (3/3)", value: "fully_approved" }];
  const payrollFacetOptions = [
    { label: "Pending", value: "pending" },
    { label: "Ready for HR", value: "ready_for_hr" },
    { label: "Ready", value: "ready" },
    { label: "Deducted", value: "deducted" },
    { label: "Failed", value: "failed" },
  ];

  const columns: ColumnDef<any, any>[] = [
    {
      id: "select",
      header: ({ table }) => {
        const allPage = table.getRowModel().rows;
        const selectable = allPage.filter((r) => r.original.declarationId);
        const allSelected = selectable.length > 0 && selectable.every((r) => r.getIsSelected());
        const someSelected = selectable.some((r) => r.getIsSelected());
        return (
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
            onChange={() => {
              const next = !allSelected;
              selectable.forEach((r) => r.toggleSelected(next));
            }}
            className="h-4 w-4 rounded border-gray-300 text-[#0A9F43] focus:ring-[#0A9F43]"
          />
        );
      },
      cell: ({ row }) => (
        row.original.declarationId ? (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={() => row.toggleSelected()}
            className="h-4 w-4 rounded border-gray-300 text-[#0A9F43] focus:ring-[#0A9F43]"
          />
        ) : null
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "memberCode",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Member ID" />,
      cell: ({ row }) => (
        <span className="font-mono text-sm text-slate-700">{row.original.memberCode}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "memberName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Member" />,
      cell: ({ row }) => (
        <button
          onClick={() => setSelectedMemberId(row.original.memberId)}
          className="text-left font-medium text-gray-900 hover:text-[#0A9F43] hover:underline cursor-pointer truncate max-w-[180px]"
        >
          {row.getValue("memberName")}
        </button>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "phone",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Phone" />,
      cell: ({ row }) => (
        <div className="truncate max-w-[160px]">
          <p className="text-gray-700">{row.original.phone || "-"}</p>
          {row.original.groupName && (
            <p className="text-xs text-gray-500">{row.original.groupName}</p>
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "minimumRequired",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Minimum" />,
      cell: ({ row }) => <span className="font-medium">{formatMoney(row.getValue("minimumRequired"))}</span>,
    },
    {
      accessorKey: "declaredAmount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Declared" />,
      cell: ({ row }) => (
        row.original.declarationId ? <span className="font-semibold">{formatMoney(row.getValue("declaredAmount"))}</span> : <span className="text-gray-400">-</span>
      ),
    },
    {
      id: "numberOfShares",
      accessorKey: "numberOfShares",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Shares" />,
      cell: ({ row }) => {
        const shares = row.original.numberOfShares || 0;
        if (!row.original.declarationId) return <span className="text-gray-400">-</span>;
        return (
          <div className="flex items-center justify-center gap-1.5">
            <Coins className="h-3.5 w-3.5 text-[#0A9F43]" />
            <span className="font-bold text-[#0A9F43]">{formatShares(shares)}</span>
          </div>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: "declaredSource",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Declared By" />,
      cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.declaredSource || "-"}</span>,
      enableSorting: false,
    },
    {
      accessorKey: "declarationStatus",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.getValue("declarationStatus") as string;
        if (!row.original.declarationId) return statusBadge("not_declared");
        const isFullyApproved = row.original.approvalCount >= row.original.requiredApprovals;
        const displayStatus = isFullyApproved ? "approved_by_leaders" : status;
        return statusBadge(displayStatus);
      },
      filterFn: "arrIncludesSome",
    },
    {
      accessorKey: "approval",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Approval" />,
      cell: ({ row }) => {
        const count = Math.min(row.original.approvalCount, row.original.requiredApprovals);
        const required = row.original.requiredApprovals;
        if (!row.original.declarationId) return <span className="text-gray-400">-</span>;
        const isFull = count >= required;
        return (
          <span className={`inline-flex items-center gap-1 text-sm font-medium ${isFull ? "text-green-700" : "text-amber-600"}`}>
            {isFull && <CheckCircle className="h-3.5 w-3.5" />}
            {count}/{required}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: "payrollStatus",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Payroll" />,
      cell: ({ row }) => {
        const isFullyApproved = row.original.approvalCount >= row.original.requiredApprovals;
        const status = isFullyApproved ? "ready_for_hr" : (row.original.payrollStatus || "pending");
        return payrollBadge(status);
      },
    },
    {
      accessorKey: "submittedAt",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Submitted" />,
      cell: ({ row }) => {
        const val = row.getValue("submittedAt");
        return val ? <span className="text-xs text-gray-600">{new Date(val as string).toLocaleString()}</span> : <span className="text-gray-400">-</span>;
      },
      sortingFn: "datetime",
    },
    {
      id: "actions",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Actions" />,
      cell: ({ row }) => {
        const member = row.original;
        const isFullyApproved = member.approvalCount >= member.requiredApprovals;
        const status = member.declarationStatus;
        const canShowApprove = member.declarationId && status === "pending_leader_approval" && !isFullyApproved && !member.alreadyApprovedByCurrentUser;
        const canShowReject = member.declarationId && status === "pending_leader_approval" && !isFullyApproved;
        const alreadyApproved = member.alreadyApprovedByCurrentUser && !isFullyApproved;

        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900"
              onClick={() => setSelectedMemberId(member.memberId)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            {canShowApprove && (
              <Button
                size="sm"
                className="h-7 px-2.5 bg-green-600 hover:bg-green-700 text-white text-xs"
                disabled={busy}
                onClick={() => runAction(async () => {
                  await api.patch(`/monthly-saving-declarations/${member.declarationId}/approve`);
                  toast.success("Declaration approved");
                })}
              >
                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                Approve
              </Button>
            )}
            {alreadyApproved && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs h-7">
                <CheckCircle className="h-3 w-3 mr-1" />
                Approved
              </Badge>
            )}
            {isFullyApproved && (
              <Badge className="bg-green-100 text-green-800 border-green-300 text-xs h-7">
                <CheckCircle className="h-3 w-3 mr-1" />
                Approved
              </Badge>
            )}
          </div>
        );
      },
      enableSorting: false,
    },
  ];

  const facets = [
    { name: "declarationStatus", title: "Status", options: statusFacetOptions },
    { name: "approval", title: "Approval", options: approvalFacetOptions },
    { name: "payrollStatus", title: "Payroll", options: payrollFacetOptions },
  ];

  const batchClosed = ["sent_to_hr", "locked", "processed"].includes(overview?.payrollBatchStatus);
  const canSendToHr = !batchClosed;

  const exportList = () => {
    const rows = allMembers.map((m: any) => ({
      "Member Code": m.memberCode,
      "Member Name": m.memberName,
      Phone: m.phone || "",
      Group: m.groupName || "",
      "Minimum Required": m.minimumRequired,
      "Declared Amount": m.declaredAmount,
      "Number of Shares": m.numberOfShares || 0,
      "Declaration Status": m.declarationStatus,
      "Approval Progress": `${Math.min(m.approvalCount, m.requiredApprovals)}/${m.requiredApprovals}`,
      "Payroll Status": m.payrollStatus,
      Source: m.declaredSource || "",
      "Submitted At": m.submittedAt ? new Date(m.submittedAt).toLocaleString() : "",
    }));
    const worksheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.sheet_add_aoa(worksheet, [
      ["IKIMINA Teachers Cooperative"],
      ["Monthly Saving Declaration Report"],
      [`Month/Year: ${month} / ${year}`],
      [`Active Members: ${totals.activeMembers || 0}`],
      [`Total Declared: ${Number(totals.totalDeclaredAmount || 0).toLocaleString()} FRW`],
      [`Total Shares: ${formatShares(totals.totalShares || 0)}`],
      [],
      ["Member Code", "Member Name", "Phone", "Group", "Minimum Required", "Declared Amount", "Number of Shares", "Declaration Status", "Approval Progress", "Payroll Status", "Source", "Submitted At"],
    ]);
    XLSX.utils.sheet_add_aoa(worksheet, rows.map((r: any) => Object.values(r)), { origin: -1 });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly declarations");
    XLSX.writeFile(workbook, `monthly-declarations-${year}-${String(month).padStart(2, "0")}.xlsx`);
  };

  if (overviewQuery.isLoading && !overview) {
    return <div className="py-16 text-center text-sm text-gray-500">Loading monthly declaration overview...</div>;
  }
  if (overviewQuery.error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">Unable to load the monthly declaration overview.</div>;
  }

  const cardData = [
    ["Active Members", totals.activeMembers || 0, Users, "text-slate-700"],
    ["Declared", totals.declaredMembers || 0, CheckCircle2, "text-green-700"],
    ["Not Declared", totals.notDeclaredMembers || 0, AlertTriangle, "text-red-700"],
    ["Awaiting Approval", totals.awaitingApproval || 0, Clock3, "text-orange-700"],
    ["Total Declared Amount", formatMoney(totals.totalDeclaredAmount || 0), null, "text-gray-900"],
    ["Total Shares", formatShares(totals.totalShares || 0), null, "text-[#0A9F43]"],
  ] as const;

  return (
    <section className="space-y-4 print:space-y-2">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-950">Monthly Declaration Overview</h2>
          <p className="mt-1 text-sm text-gray-600">Monitor every active member before the payroll list is finalized.</p>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          {batchClosed ? (
            <Button size="sm" variant="outline" disabled={busy || overview?.payrollBatchStatus === "processed"} onClick={async () => {
              await runAction(async () => {
                await api.post(`/payroll/monthly-saving-batches/${month}/${year}/reopen`);
                toast.success("Payroll list reopened");
              });
            }}><RotateCcw className="mr-1.5 h-4 w-4" />Reopen List</Button>
          ) : (
            <div className="relative group inline-block">
              <Button size="sm" disabled={busy || !canSendToHr} onClick={async () => {
                await runAction(async () => {
                  const res = await api.post(`/payroll/monthly-saving-batches/${month}/${year}/send-to-hr`);
                  const data = res.data;
                  toast.success(data.message || "Payroll list sent to HR");
                });
              }} className="bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"><Send className="mr-1.5 h-4 w-4" />Close & Send to HR</Button>
              {!canSendToHr && overview?.blockingReason && (
                <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-max -translate-x-1/2 rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">{overview.blockingReason}</div>
              )}
            </div>
          )}
          <Button size="sm" variant="outline" onClick={exportList}><Download className="mr-1.5 h-4 w-4" />Export</Button>
        </div>
      </div>

      <div className="rounded-lg border border-[#0A9F43]/20 bg-[#0A9F43]/5 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0A9F43]/10">
            <Info className="h-3.5 w-3.5 text-[#0A9F43]" />
          </div>
          <div className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">1 Share = 10,000 FRW</span>
            <p className="text-xs text-gray-500 mt-0.5">
              Examples: 10,000 FRW &rarr; 1 Share &nbsp;&bull;&nbsp; 15,000 FRW &rarr; 1.5 Shares &nbsp;&bull;&nbsp; 22,000 FRW &rarr; 2.2 Shares
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 xl:grid-cols-6">
        {cardData.map(([label, value, Icon, color]) => (
          <Card key={label} className="border-gray-200 shadow-sm">
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <p className="text-xs font-medium text-gray-500">{label}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{value || 0}</p>
              </div>
              {Icon && <Icon className={`h-5 w-5 ${color}`} />}
            </CardContent>
          </Card>
        ))}
      </div>

      <DataTable
        isFetching={overviewQuery.isFetching}
        defaultColumnVisibility={{}}
        isLoading={overviewQuery.status === "loading"}
        data={paginatedMembers}
        columns={columns}
        onSearch={(e: string) => { setSearchText(e); setPagination((prev) => ({ ...prev, pageIndex: 0 })); }}
        sorting={sorting}
        setSorting={setSorting}
        pageCount={pageCount}
        setPagination={setPagination}
        pageIndex={pageIndex}
        pageSize={pageSize}
        setColumnFilters={setColumnFilters}
        columnFilters={columnFilters}
        facets={facets}
        bulkApprove={handleBulkApprove}
      />

      <Dialog open={!!declareTarget} onOpenChange={(open) => { if (!open) setDeclareTarget(null); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#0A9F43]" />
              Declare for {declareTarget?.memberName || "Member"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-xs text-gray-500">{declareTarget?.memberCode}</p>
              <p className="text-sm text-gray-500">Minimum required: {formatMoney(declareTarget?.minimumRequired || 0)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Declared Amount (FRW)</label>
              <input
                type="number"
                value={declareAmount}
                min={declareTarget?.minimumRequired || 0}
                onChange={(e) => setDeclareAmount(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0A9F43] focus:ring-1 focus:ring-[#0A9F43] outline-none"
              />
              {declareAmount > 0 && (
                <div className="mt-2 flex items-center gap-2 text-sm text-[#0A9F43] bg-[#0A9F43]/5 rounded-md px-3 py-2">
                  <Coins className="h-4 w-4" />
                  <span className="font-semibold">{formatShares(declareAmount / SHARE_PRICE)} shares</span>
                  <span className="text-gray-500 text-xs">({formatMoney(SHARE_PRICE)} per share)</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeclareTarget(null)}>Cancel</Button>
            <Button
              size="sm"
              className="bg-[#0A9F43] text-white hover:bg-[#088536]"
              disabled={busy || declareAmount < (declareTarget?.minimumRequired || 0)}
              onClick={() => {
                if (!declareTarget) return;
                const target = declareTarget;
                setDeclareTarget(null);
                runAction(async () => {
                  await api.post("/monthly-saving-declarations/bulk-declare", {
                    month,
                    year,
                    declaredAmount: declareAmount,
                    memberIds: [target.memberId],
                  });
                  toast.success(`Declaration created for ${target.memberName}`);
                });
              }}
            >
              Confirm Declaration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDeclareOpen} onOpenChange={(open) => { if (!open) { setBulkDeclareOpen(false); setBulkDeclareSelected(new Set()); } }}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#0A9F43]" />
              Bulk Declare Monthly Savings
            </DialogTitle>
            <p className="text-sm text-gray-500">Select members and set the amount to declare.</p>
          </DialogHeader>
          <div className="space-y-3 flex-1 overflow-y-auto py-2">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 shrink-0">Amount per member (FRW):</label>
              <input
                type="number"
                value={bulkDeclareAmount}
                min={0}
                onChange={(e) => setBulkDeclareAmount(Number(e.target.value))}
                className="w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0A9F43] focus:ring-1 focus:ring-[#0A9F43] outline-none"
              />
              {bulkDeclareAmount > 0 && (
                <div className="flex items-center gap-1.5 text-sm text-[#0A9F43]">
                  <Coins className="h-4 w-4" />
                  <span className="font-semibold">{formatShares(bulkDeclareAmount / SHARE_PRICE)} shares/member</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-sm text-gray-500">{bulkDeclareSelected.size} of {allMembers.filter((m: any) => m.canDeclareForMember).length} selected</span>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => {
                  const declarable = allMembers.filter((m: any) => m.canDeclareForMember);
                  if (bulkDeclareSelected.size === declarable.length) {
                    setBulkDeclareSelected(new Set());
                  } else {
                    setBulkDeclareSelected(new Set(declarable.map((m: any) => m.memberId)));
                  }
                }}
              >
                {bulkDeclareSelected.size === allMembers.filter((m: any) => m.canDeclareForMember).length ? "Deselect All" : "Select All"}
              </Button>
            </div>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {allMembers.filter((m: any) => m.canDeclareForMember).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">All members have already declared for this month.</p>
              ) : (
                allMembers.filter((m: any) => m.canDeclareForMember).map((member: any) => (
                  <label key={member.memberId} className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkDeclareSelected.has(member.memberId)}
                      onChange={() => {
                        const next = new Set(bulkDeclareSelected);
                        if (next.has(member.memberId)) next.delete(member.memberId);
                        else next.add(member.memberId);
                        setBulkDeclareSelected(next);
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-[#0A9F43] focus:ring-[#0A9F43]"
                    />
                    <span className="text-xs font-mono text-gray-500 w-20">{member.memberCode}</span>
                    <span className="text-sm font-medium text-gray-900 flex-1">{member.memberName}</span>
                    <span className="text-xs text-gray-400">{member.groupName}</span>
                  </label>
                ))
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 border-t pt-3">
            <Button variant="outline" size="sm" onClick={() => { setBulkDeclareOpen(false); setBulkDeclareSelected(new Set()); }}>Cancel</Button>
            <Button
              size="sm"
              className="bg-[#0A9F43] text-white hover:bg-[#088536]"
              disabled={busy || bulkDeclareSelected.size === 0 || bulkDeclareAmount <= 0}
              onClick={() => {
                const memberIds = Array.from(bulkDeclareSelected);
                setBulkDeclareOpen(false);
                setBulkDeclareSelected(new Set());
                runAction(async () => {
                  const { data } = await api.post("/monthly-saving-declarations/bulk-declare", {
                    month,
                    year,
                    declaredAmount: bulkDeclareAmount,
                    memberIds,
                  });
                  const results = data?.data || [];
                  const successCount = results.filter((r: any) => r.success).length;
                  const failCount = results.filter((r: any) => !r.success).length;
                  if (failCount > 0) {
                    toast.error(`Declared for ${successCount} member(s), ${failCount} failed`);
                  } else {
                    toast.success(`Declared for ${successCount} member(s)`);
                  }
                });
              }}
            >
              Declare for {bulkDeclareSelected.size} member{bulkDeclareSelected.size !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!selectedMemberId} onOpenChange={(open) => { if (!open) setSelectedMemberId(null); }}>
        <SheetContent className="w-full sm:max-w-[600px] p-0 flex flex-col">
          <SheetHeader className="sticky top-0 z-10 bg-white border-b px-6 py-4">
            <SheetTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <User className="h-5 w-5 text-[#0A9F43]" />
              {selectedMemberData?.member?.fullNames || "Member Details"}
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="flex-1 p-6">
            {memberHistoryQuery.isLoading ? (
              <div className="py-16 text-center text-sm text-gray-500">Loading member details...</div>
            ) : selectedMemberData ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                  <div><p className="text-xs text-gray-500">Code</p><p className="font-medium">{selectedMemberData.member.memberCode}</p></div>
                  <div><p className="text-xs text-gray-500">Phone</p><p className="font-medium">{selectedMemberData.member.phone || "-"}</p></div>
                  <div><p className="text-xs text-gray-500">Email</p><p className="font-medium">{selectedMemberData.member.email || "-"}</p></div>
                  <div><p className="text-xs text-gray-500">Group</p><p className="font-medium">{selectedMemberData.member.groupName || selectedMemberData.member.department || "-"}</p></div>
                  <div><p className="text-xs text-gray-500">Gender</p><p className="font-medium">{selectedMemberData.member.gender || "-"}</p></div>
                  <div><p className="text-xs text-gray-500">Status</p><p className="font-medium">{selectedMemberData.member.membershipStatus || "-"}</p></div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Current Month Declaration</h3>
                  {selectedMemberData.currentDeclaration ? (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><p className="text-gray-500">Amount</p><p className="font-semibold">{formatMoney(selectedMemberData.currentDeclaration.declaredAmount)}</p></div>
                      <div><p className="text-gray-500">Status</p><p>{statusBadge(selectedMemberData.currentDeclaration.status)}</p></div>
                      <div><p className="text-gray-500">Approval</p><p className="font-medium">{selectedMemberData.currentDeclaration.approvalCount}/{selectedMemberData.currentDeclaration.requiredApprovals}</p></div>
                      <div><p className="text-gray-500">Payroll</p><p>{payrollBadge(selectedMemberData.currentDeclaration.payrollStatus)}</p></div>
                      <div><p className="text-gray-500">Submitted By</p><p className="font-medium">{selectedMemberData.currentDeclaration.createdBy || "-"}</p></div>
                      <div><p className="text-gray-500">Submitted At</p><p className="font-medium">{selectedMemberData.currentDeclaration.submittedAt ? new Date(selectedMemberData.currentDeclaration.submittedAt).toLocaleString() : "-"}</p></div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No declaration for this month.</p>
                  )}
                </div>

                {selectedMemberData.currentDeclaration?.approvals?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Approval Timeline</h3>
                      <ul className="space-y-2">
                        {selectedMemberData.currentDeclaration.approvals.map((a: any) => (
                          <li key={a.id} className="flex items-center justify-between text-sm bg-green-50 rounded-md px-3 py-2">
                            <span className="font-medium text-green-800">{a.leaderRole}</span>
                            <span className="text-green-700">{a.leaderName || "Approved"} &middot; {a.createdAt ? new Date(a.createdAt).toLocaleString() : ""}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}

                {selectedMemberData.previousDeclarations?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Previous Declarations</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedMemberData.previousDeclarations.map((d: any) => (
                          <div key={d.id} className="flex items-center justify-between text-sm bg-gray-50 rounded-md px-3 py-2">
                            <span className="font-medium">Month {d.month + 1} / {d.year}</span>
                            <span>{formatMoney(d.declaredAmount)}</span>
                            <span>{statusBadge(d.status)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {selectedMemberData.contributions?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Recent Contributions</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedMemberData.contributions.slice(0, 5).map((c: any) => (
                          <div key={c.id} className="flex items-center justify-between text-sm bg-gray-50 rounded-md px-3 py-2">
                            <span>{c.contributionDate ? new Date(c.contributionDate).toLocaleDateString() : "-"}</span>
                            <span className="font-semibold">{formatMoney(c.amount)}</span>
                            <Badge variant="outline" className={`text-xs ${c.isVerified ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                              {c.isVerified ? "Verified" : "Pending"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {selectedMemberData.loans?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Loans</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedMemberData.loans.map((l: any) => (
                          <div key={l.id} className="flex items-center justify-between text-sm bg-gray-50 rounded-md px-3 py-2">
                            <span className="font-semibold">{formatMoney(l.amount)}</span>
                            <span>Due: {formatMoney(l.dueAmount || 0)}</span>
                            <span>Paid: {formatMoney(l.totalPaid || 0)}</span>
                            <Badge variant="outline" className="text-xs">{l.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {selectedMemberData.penalties?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Penalties / Fines</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedMemberData.penalties.map((p: any) => (
                          <div key={p.id} className="flex items-center justify-between text-sm bg-red-50 rounded-md px-3 py-2">
                            <span className="font-medium">{p.penaltyType.replace("_", " ")}</span>
                            <span className="font-semibold">{formatMoney(p.amount)}</span>
                            <span className="text-xs text-gray-500">{p.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="py-16 text-center text-sm text-gray-400">No data available for this member.</div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </section>
  );
}
